import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from '../lib/gsapConfig';
import { Tiles } from './ui/Tiles';

const services = [
  { title: 'Local moving', description: 'Same-state moves from one city to another, apartments, condos, and homes.', image: '/services/local-moving.jpg' },
  { title: 'Long-distance moving', description: 'State-to-state routes with movers who handle longer hauls and timing windows.', image: '/services/out-of-state-moving.jpg' },
  { title: 'Residential moving', description: 'Home moving help for renters, homeowners, families, and first-time movers.', image: '/services/residential-moving.jpg' },
  { title: 'Commercial moving', description: 'Office and business moves with weekend scheduling and equipment handling.', image: '/services/commercial-moving.png' },
  { title: 'Packaging', description: 'Packing, labeling, item protection, and unpacking support when you need it.', image: '/services/packaging.png' },
  { title: 'Trucking support', description: 'Transportation support for larger loads, routes, and shipment coordination.', image: '/services/trucking-support.jpg' },
];

const Services = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.services-title-word', {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 78%',
        },
        y: 54,
        opacity: 0,
        duration: 0.75,
        stagger: 0.06,
        ease: 'power4.out',
      });

      gsap.from('.service-card', {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 68%',
        },
        y: 34,
        opacity: 0,
        duration: 0.55,
        stagger: 0.06,
        ease: 'power3.out',
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative overflow-hidden bg-slate-50 py-14 sm:py-16" style={{ '--tile': 'rgba(235, 152, 19, 0.2)' }}>
      <div className="absolute inset-x-0 top-0 h-full overflow-hidden">
        <div className="absolute left-1/2 top-1/2 h-[118%] w-[118%] -translate-x-1/2 -translate-y-1/2 opacity-95 [mask-image:radial-gradient(ellipse_at_center,black_0%,black_48%,transparent_76%)]">
          <Tiles rows={42} cols={9} tileSize="md" tileClassName="border-slate-300/75" />
        </div>
      </div>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(235,152,19,0.2),transparent_28%),radial-gradient(circle_at_82%_22%,rgba(59,130,246,0.08),transparent_30%),linear-gradient(180deg,rgba(248,250,252,0.26),rgba(248,250,252,0.72)_46%,rgba(248,250,252,0.96))]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-slate-50 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-slate-50 to-transparent" />

      <div className="container relative z-10 mx-auto px-4">
        <div className="mb-10 max-w-3xl">
          <span className="inline-flex rounded-full bg-white px-4 py-2 text-sm font-black text-primary shadow-sm">
            Moving services
          </span>
          <h2 className="gsap-perspective-parent mt-4 text-3xl font-black tracking-normal text-slate-950 sm:text-5xl">
            {['Choose', 'the', 'right', 'move', 'type'].map((word) => (
              <span key={word} className="hero-clip-overflow mr-2 inline-block pb-1">
                <span className="services-title-word inline-block">{word}</span>
              </span>
            ))}
          </h2>
          <p className="mt-4 text-lg leading-8 text-slate-600">
From packing to delivery, explore services designed to make your move easier and safer.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {services.map((service) => (
            <Link key={service.title} to="/quote" className="service-card group block">
              <div className="h-full rounded-3xl border border-white/80 bg-white/92 p-4 shadow-[0_14px_40px_rgba(15,23,42,0.08)] backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:border-primary/40 hover:bg-white hover:shadow-[0_18px_46px_rgba(15,23,42,0.12)]">
                <div className="relative h-44 overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-100">
                  <img
                    src={service.image}
                    alt={`${service.title} service`}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/10 via-transparent to-white/5" />
                </div>
                <div className="p-2 pt-5">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-xl font-black text-slate-950">{service.title}</h3>
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-white">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{service.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
